/**
 * About Page Component
 * Company story, values, vision & mission, who we serve
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import './About.css';

// Values data
const values = [
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
        ),
        title: 'Reliability',
        description: 'We deliver on our promises. Every engagement is backed by our commitment to quality and consistency.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4M12 8h.01" />
            </svg>
        ),
        title: 'Transparency',
        description: 'No hidden fees, no surprises. Clear communication and honest pricing from day one.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
        ),
        title: 'Partnership',
        description: 'We\'re not just vendors. We invest in long-term relationships and mutual success.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
        ),
        title: 'Excellence',
        description: 'From developer vetting to code quality, we maintain the highest standards in everything we do.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
            </svg>
        ),
        title: 'Local Focus',
        description: 'Based in Malaysia, built for Malaysian businesses. We understand the local market and timezone.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="13 17 18 12 13 7" />
                <polyline points="6 17 11 12 6 7" />
            </svg>
        ),
        title: 'Innovation',
        description: 'Continuously improving our model to deliver better outcomes for our clients.',
    },
];

// Who we serve data
const serveData = [
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4M9 9v.01M9 12v.01M9 15v.01M9 18v.01" />
            </svg>
        ),
        title: 'Enterprise IT Leaders',
        description: 'CIOs, CTOs, and IT directors who need to scale development capacity without the overhead of permanent hires. We provide enterprise-grade talent with the flexibility you need.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
            </svg>
        ),
        title: 'SME Owners & Founders',
        description: 'Business owners who need development expertise but don\'t have the technical knowledge to hire. We handle the vetting so you can focus on your business.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
        ),
        title: 'Project Managers',
        description: 'Teams with specific project deadlines who need reliable developers that can hit the ground running. Our onboarding is designed for speed.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
        ),
        title: 'Finance Leaders',
        description: 'CFOs who need predictable, controllable IT costs. Our subscription model provides clear budgeting with no surprises.',
    },
];

function About() {
    return (
        <>
            <Helmet>
                <title>About Us | CODEMAKER Technology - Malaysia's First TaaS</title>
                <meta
                    name="description"
                    content="Learn about CODEMAKER Technology, Malaysia's first Talent-as-a-Service platform. Discover our mission to make enterprise-grade development accessible to all Malaysian businesses."
                />
            </Helmet>

            <main>
                {/* Hero Section */}
                <section className="about-hero">
                    <div className="about-hero__container">
                        <span className="about-hero__badge">About CODEMAKER</span>
                        <h1 className="about-hero__title">Malaysia's First TaaS Platform</h1>
                        <p className="about-hero__description">
                            We're on a mission to revolutionize how Malaysian businesses access
                            top-tier development talent.
                        </p>
                    </div>
                </section>

                {/* Story Section */}
                <Section>
                    <div className="story">
                        <div className="story__content">
                            <h2>Our Story</h2>
                            <p>
                                CODEMAKER Technology was founded with a simple observation: Malaysian businesses,
                                especially SMEs, struggle to access quality software development talent. The
                                traditional hiring process is expensive, time-consuming, and requires technical
                                expertise that many business owners don't have.
                            </p>
                            <p>
                                We created the Talent-as-a-Service (TaaS) model to solve this. Instead of hiring
                                full-time developers with all the associated overhead, businesses can now rent
                                managed developers on a subscription basis—complete with CTO oversight to ensure
                                quality.
                            </p>
                            <p>
                                Today, we're proud to be Malaysia's first dedicated TaaS platform, serving
                                enterprises and SMEs across industries. Our model has helped dozens of
                                businesses accelerate their digital transformation without breaking the bank.
                            </p>
                        </div>
                        <div className="story__visual">
                            <img
                                src={require('../assets/images/codemaker-logo-box.png')}
                                alt="CODEMAKER Technology Logo"
                                className="story__image"
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    borderRadius: 'var(--radius-lg)',
                                    boxShadow: 'var(--shadow-lg)'
                                }}
                            />
                        </div>
                    </div>
                </Section>

                {/* Positioning Statement */}
                <Section background="light">
                    <div className="positioning">
                        <p className="positioning__text">
                            "Malaysia's first Talent-as-a-Service platform focused on managed developers
                            for digital transformation—making enterprise-grade development accessible to
                            businesses of all sizes."
                        </p>
                    </div>
                </Section>

                {/* Vision & Mission */}
                <Section>
                    <Section.Header
                        subtitle="Our Purpose"
                        title="Vision & Mission"
                        description="What drives us every day."
                    />

                    <div className="vm-grid">
                        <div className="vm-card vm-card--vision">
                            <span className="vm-card__label">Our Vision</span>
                            <h3 className="vm-card__title">Democratizing Tech Talent</h3>
                            <p className="vm-card__text">
                                To revolutionize how businesses hire IT experts and make accessing
                                top-tier technical talent as easy as subscribing to a service. We envision
                                a future where every Malaysian business can build world-class software.
                            </p>
                        </div>
                        <div className="vm-card">
                            <span className="vm-card__label">Our Mission</span>
                            <h3 className="vm-card__title">Reliable, Managed Talent</h3>
                            <p className="vm-card__text">
                                To provide reliable, managed, on-demand development talent with minimal
                                friction. We handle the complexity of technical hiring, vetting, and
                                quality assurance so our clients can focus on what they do best.
                            </p>
                        </div>
                    </div>
                </Section>

                {/* Our Values */}
                <Section background="light">
                    <Section.Header
                        subtitle="What We Believe"
                        title="Our Core Values"
                        description="The principles that guide everything we do."
                    />

                    <div className="values-grid">
                        {values.map((value, index) => (
                            <div key={index} className="value-card">
                                <div className="value-card__icon">{value.icon}</div>
                                <h3 className="value-card__title">{value.title}</h3>
                                <p className="value-card__description">{value.description}</p>
                            </div>
                        ))}
                    </div>
                </Section>

                {/* Who We Serve */}
                <Section>
                    <Section.Header
                        subtitle="Our Clients"
                        title="Who We Serve"
                        description="Built for the people who drive Malaysian business forward."
                    />

                    <div className="serve-grid">
                        {serveData.map((item, index) => (
                            <div key={index} className="serve-card">
                                <div className="serve-card__icon">{item.icon}</div>
                                <div className="serve-card__content">
                                    <h3 className="serve-card__title">{item.title}</h3>
                                    <p className="serve-card__description">{item.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </Section>

                {/* CTA Section */}
                <Section background="gradient" spacing="large">
                    <div className="cta">
                        <h2 className="cta__title">Partner With Us Today</h2>
                        <p className="cta__description">
                            Join the growing number of Malaysian businesses building faster with CODEMAKER.
                            Let's discuss how we can help you achieve your goals.
                        </p>
                        <div className="cta__actions">
                            <Button as={Link} to="/contact" variant="primary" size="large">
                                Get in Touch
                            </Button>
                            <Button as={Link} to="/how-it-works" variant="ghost" size="large">
                                Learn How It Works
                            </Button>
                        </div>
                    </div>
                </Section>
            </main>
        </>
    );
}

export default About;
